import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography, Button, Paper } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

const PublicResultadosView = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState(null);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  const containerRef = useRef(null);

  const backendUrl = process.env.REACT_APP_API_URL || "https://srv1866478.hstgr.cloud";

  // 1. Obtener la información o PDF desde el Backend
  useEffect(() => {
    const cargarResultados = async () => {
      if (!token) {
        setError("Token no válido");
        setLoading(false);
        return;
      }

      try {
        const isQrRoute = typeof window !== "undefined" && window.location.pathname.includes("/consultaweb/qr/");
        const primaryEndpoint = isQrRoute
          ? `${backendUrl.replace(/\/$/, "")}/consultaweb/qr/${token}`
          : `${backendUrl.replace(/\/$/, "")}/consultaweb/ticket/${token}`;
        const secondaryEndpoint = isQrRoute
          ? `${backendUrl.replace(/\/$/, "")}/consultaweb/ticket/${token}`
          : `${backendUrl.replace(/\/$/, "")}/consultaweb/qr/${token}`;

        let res = await fetch(primaryEndpoint);
        let contentType = res.ok ? (res.headers.get("content-type") || "") : "";

        // Si el endpoint primario no devolvió PDF (ej. devolvió HTML o error), buscamos en el secundario
        if (!contentType.includes("application/pdf")) {
          try {
            const resSec = await fetch(secondaryEndpoint);
            if (resSec.ok) {
              const secContentType = resSec.headers.get("content-type") || "";
              if (secContentType.includes("application/pdf")) {
                res = resSec;
                contentType = secContentType;
              } else if (!res.ok) {
                res = resSec;
                contentType = secContentType;
              }
            }
          } catch (eSec) {
            console.warn("Consulta a endpoint secundario:", eSec);
          }
        }

        if (!res.ok) {
          if (res.status === 403) {
            setError("Enlace de consulta inválido, alterado o expirado por seguridad.");
          } else if (res.status === 404) {
            setError("La solicitud especificada no existe o fue eliminada.");
          } else {
            setError("Ocurrió un problema al consultar los resultados.");
          }
          setLoading(false);
          return;
        }

        contentType = res.headers.get("content-type") || contentType || "";

        if (contentType.includes("application/pdf")) {
          const buffer = await res.arrayBuffer();
          setPdfArrayBuffer(buffer);

          const blob = new Blob([buffer], { type: "application/pdf" });
          setPdfDownloadUrl(URL.createObjectURL(blob));
        } else {
          const text = await res.text();
          setHtmlContent(text);
        }
      } catch (err) {
        console.error("Error consultando QR:", err);
        setError("Error de comunicación con el servidor.");
      } finally {
        setLoading(false);
      }
    };

    cargarResultados();
  }, [token, backendUrl]);

  // 2. Dibujar las páginas del PDF sobre Canvas para visualización instantánea en móviles
  useEffect(() => {
    if (!pdfArrayBuffer || !containerRef.current) return;

    let isMounted = true;

    const renderPdf = async () => {
      try {
        if (!window.pdfjsLib) {
          await new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            script.onload = resolve;
            document.body.appendChild(script);
          });
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        }

        const pdf = await window.pdfjsLib.getDocument({ data: new Uint8Array(pdfArrayBuffer) }).promise;
        if (!isMounted || !containerRef.current) return;

        const container = containerRef.current;
        container.innerHTML = "";

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const screenWidth = window.innerWidth || 360;
          const scale = screenWidth < 600 ? (screenWidth - 32) / page.getViewport({ scale: 1 }).width : 1.4;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.style.margin = "12px auto";
          canvas.style.display = "block";
          canvas.style.boxShadow = "0 4px 15px rgba(0,0,0,0.12)";
          canvas.style.maxWidth = "100%";
          canvas.style.borderRadius = "8px";
          canvas.style.backgroundColor = "#ffffff";

          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({ canvasContext: context, viewport: viewport }).promise;
          if (isMounted) {
            container.appendChild(canvas);
          }
        }
      } catch (errRender) {
        console.error("Error al renderizar páginas del PDF:", errRender);
      }
    };

    renderPdf();

    return () => {
      isMounted = false;
    };
  }, [pdfArrayBuffer]);

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f7f9",
      }}
    >
      {loading && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <CircularProgress sx={{ color: "#18234f", mb: 2 }} />
          <Typography variant="body1" sx={{ color: "#555" }}>
            Consultando estado de los estudios...
          </Typography>
        </Box>
      )}

      {!loading && error && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            minHeight: "80vh",
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              maxWidth: 440,
              width: "100%",
              borderTop: "6px solid #d32f2f",
            }}
          >
            <Typography variant="h6" sx={{ color: "#18234f", fontWeight: "bold", mb: 2 }}>
              LABORATORIOS GARCÍA
            </Typography>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          </Paper>
        </Box>
      )}

      {!loading && !error && pdfArrayBuffer && (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          {/* Barra Superior */}
          <Box
            sx={{
              backgroundColor: "#18234f",
              color: "#fff",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              top: 0,
              zIndex: 10,
              boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
              Informe de Resultados - Laboratorios García
            </Typography>
            {pdfDownloadUrl && (
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                href={pdfDownloadUrl}
                download={`Informe_Resultados.pdf`}
                sx={{ backgroundColor: "#25D366", "&:hover": { backgroundColor: "#1ebd59" } }}
              >
                Descargar
              </Button>
            )}
          </Box>

          {/* Renderizado directo de páginas en Canvas */}
          <Box
            ref={containerRef}
            sx={{
              p: { xs: 1, sm: 3 },
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
            }}
          >
            <CircularProgress sx={{ color: "#18234f", my: 4 }} />
          </Box>
        </Box>
      )}

      {!loading && !error && htmlContent && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
    </Box>
  );
};

export default PublicResultadosView;
